import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { MenuCard } from "../menu/MenuCard";
import { MenuCardLabel, MenuCardLabelSuffix } from "../menu/MenuCardLabel";
import { InputWithMargin } from "../editor/Input";
import { MenuCardScrollContainer } from "../menu/MenuCardScrollContainer";
import Highlighter from "react-highlight-words";
import { UiConstants } from "../../data/UiConstants";
import { AiOutlineSearch } from "react-icons/ai";
import { useZoomPanHelper } from "react-flow-renderer";
import { CgEditBlackPoint } from "react-icons/cg";
import { actionEditorStore } from "../../stores/ActionEditorStore";
import { getActionShortDescriptionForActionEditor } from "../../helper/actionEditorHelpers";
import { actionNodeIcon } from "./actionNodeData";
import { Heading2Base } from "../shared/Heading";
const Container = styled(MenuCard).withConfig({
  displayName: "ActionSearchMenu__Container",
  componentId: "sc-1k4beuu-0"
})(["width:300px;"]);
const SingleSearchResult = styled.div.withConfig({
  displayName: "ActionSearchMenu__SingleSearchResult",
  componentId: "sc-1k4beuu-1"
})(["background-color:", ";display:flex;flex-direction:column;margin:2px;border-radius:", ";border:1px solid black;"], props => props.color ? props.color : "#FFFFFF", UiConstants.BORDER_RADIUS);
const SearchIcon = styled(AiOutlineSearch).withConfig({
  displayName: "ActionSearchMenu__SearchIcon",
  componentId: "sc-1k4beuu-2"
})(["display:flex;flex-grow:0;flex-shrink:1;"]);
const SearchRow = styled.div.withConfig({
  displayName: "ActionSearchMenu__SearchRow",
  componentId: "sc-1k4beuu-3"
})(["display:flex;flex-direction:row;justify-content:center;align-items:center;"]);
const SearchInput = styled(InputWithMargin).withConfig({
  displayName: "ActionSearchMenu__SearchInput",
  componentId: "sc-1k4beuu-4"
})(["flex-grow:1;flex-shrink:0;"]);
const TitleContainer = styled(Heading2Base).withConfig({
  displayName: "ActionSearchMenu__TitleContainer",
  componentId: "sc-1k4beuu-5"
})(["display:flex;flex-direction:row;padding:4px;justify-content:center;align-items:center;"]);
const TileIcon = styled.div.withConfig({
  displayName: "ActionSearchMenu__TileIcon",
  componentId: "sc-1k4beuu-6"
})(["display:flex;flex-grow:0;flex-shrink:1;margin:4px;"]);
const TitleHighlighter = styled(Highlighter).withConfig({
  displayName: "ActionSearchMenu__TitleHighlighter",
  componentId: "sc-1k4beuu-7"
})(["font-size:large;flex-grow:1;flex-shrink:0;"]);
const IdContainer = styled.div.withConfig({
  displayName: "ActionSearchMenu__IdContainer",
  componentId: "sc-1k4beuu-8"
})(["display:flex;flex-direction:row;margin:4px;font-size:small;"]);
const IdHighlighter = styled(Highlighter).withConfig({
  displayName: "ActionSearchMenu__IdHighlighter",
  componentId: "sc-1k4beuu-9"
})(["font-family:monospace;margin-left:4px;"]);
const DescriptionHighlighter = styled(Highlighter).withConfig({
  displayName: "ActionSearchMenu__DescriptionHighlighter",
  componentId: "sc-1k4beuu-10"
})(["margin-left:4px;"]);
const SearchResultContainer = styled(MenuCardScrollContainer).withConfig({
  displayName: "ActionSearchMenu__SearchResultContainer",
  componentId: "sc-1k4beuu-11"
})(["display:flex;flex-direction:column;max-height:400px;"]);
const JumpButton = styled(CgEditBlackPoint).withConfig({
  displayName: "ActionSearchMenu__JumpButton",
  componentId: "sc-1k4beuu-12"
})(["background-color:#FFFFFF;width:20px;height:20px;cursor:pointer;&:hover{background-color:", ";}"], UiConstants.COLOR_HOVER);
const SearchEventMinIntervalTimeMS = 1000;
export const ActionSearchMenu = observer(({
  property
}) => {
  const {
    t
  } = useTranslation();
  const [searchResult, setSearchResult] = useState([]);
  const [lastKeyEventTime, setLastKeyEventTime] = useState(-1);
  const [timeoutId, setTimeoutId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const zoomPanHelper = useZoomPanHelper();

  function onSearchTextChange(text) {
    const timeSinceLastKeyEvent = Date.now() - lastKeyEventTime;

    if (lastKeyEventTime < 0 || timeSinceLastKeyEvent > SearchEventMinIntervalTimeMS) {
      if (timeoutId) clearTimeout(timeoutId);
      setSearchText(text);
      setSearchResult(actionEditorStore.searchActionNodes(text, t));
      setLastKeyEventTime(Date.now());
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      const id = setTimeout(() => onSearchTextChange(text), SearchEventMinIntervalTimeMS - timeSinceLastKeyEvent);
      setTimeoutId(id);
    }
  }

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(MenuCardLabel, null, t("action_editor.search_node"), /*#__PURE__*/React.createElement(MenuCardLabelSuffix, null, "(" + searchResult.length + ")")), /*#__PURE__*/React.createElement(SearchRow, null, /*#__PURE__*/React.createElement(SearchIcon, null), /*#__PURE__*/React.createElement(SearchInput, {
    type: "text",
    onChange: ({
      target
    }) => onSearchTextChange(target.value)
  })), searchResult.length > 0 && /*#__PURE__*/React.createElement(SearchResultContainer, null, searchResult.map((action, index) => /*#__PURE__*/React.createElement(SingleSearchResult, {
    key: index,
    color: action.nodeColor()
  }, /*#__PURE__*/React.createElement(TitleContainer, null, /*#__PURE__*/React.createElement(TileIcon, null, actionNodeIcon(action)), /*#__PURE__*/React.createElement(TitleHighlighter, {
    searchWords: [searchText],
    autoEscape: true,
    textToHighlight: t(action.title())
  }), /*#__PURE__*/React.createElement(JumpButton, {
    onClick: () => actionEditorStore.jumpToAction(action, zoomPanHelper)
  })), /*#__PURE__*/React.createElement(DescriptionHighlighter, {
    searchWords: [searchText],
    autoEscape: true,
    textToHighlight: getActionShortDescriptionForActionEditor(action, t)
  }), /*#__PURE__*/React.createElement(IdContainer, null, "id:", /*#__PURE__*/React.createElement(IdHighlighter, {
    searchWords: [searchText],
    autoEscape: true,
    textToHighlight: action.$modelId
  }))))));
});